import { t, Trans } from '@lingui/macro'
import { useLingui } from '@lingui/react'
import AppBar from '@mui/material/AppBar'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Container from '@mui/material/Container'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Toolbar from '@mui/material/Toolbar'
import Typography from '@mui/material/Typography'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import LogoutIcon from '@mui/icons-material/Logout'

import { Language } from '@/layouts/main/ui/Header/Language'
import { useAppDispatch, useAppSelector } from '@/redux/rootReducer'
import { logout, selectAuthUser } from '@/redux/reducers/auth.reducer'
import { useCreateEmergencyPatient } from '../model/useCreateEmergencyPatient'
import { useSimplifiedReception } from '../model/useSimplifiedReception'
import { ActionChoice } from './ActionChoice'
import { DoneStep } from './DoneStep'
import { ExistingPatientStep } from './ExistingPatientStep'
import { NewPatientStep } from './NewPatientStep'
import { WaitingPatientsList } from './WaitingPatientsList'
import { WaitingRoomStep } from './WaitingRoomStep'

/**
 * Locked interface of the simplified reception mode: only create / find / emergency patient,
 * then put the patient in a waiting room.
 */
export function SimplifiedReceptionPage() {
  useLingui()
  const dispatch = useAppDispatch()
  const authUser = useAppSelector(selectAuthUser)
  const { step, patient, waitingRoomName, consultationId, start, choosePatient, finish, restart } = useSimplifiedReception()
  const [createEmergencyPatient, isCreatingEmergency] = useCreateEmergencyPatient()
  const handleEmergency = async () => {
    const emergencyPatient = await createEmergencyPatient()
    if (emergencyPatient) choosePatient(emergencyPatient)
  }
  const titles = { choice: t`Reception`, 'new-patient': t`New patient`, 'existing-patient': t`Existing patient`, 'waiting-room': t`Waiting room`, done: t`Done` }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'grey.100' }}>
      <AppBar position="static" elevation={0}>
        <Toolbar sx={{ gap: 2 }}>
          <Typography variant="h6" color="inherit" sx={{ flexGrow: 1 }}><Trans>MedConnect reception</Trans></Typography>
          <Language />
          <Typography color="inherit">{authUser?.userInfo ? `${authUser.userInfo.name} ${authUser.userInfo.surname}` : ''}</Typography>
          <Button color="inherit" startIcon={<LogoutIcon />} onClick={() => dispatch(logout())}><Trans>Log out</Trans></Button>
        </Toolbar>
      </AppBar>
      <Container component="main" id="main" maxWidth="md" sx={{ py: 4 }}>
        <Paper sx={{ p: 4 }}>
          <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
            {step !== 'choice' && step !== 'done' && (
              <Button startIcon={<ArrowBackIcon />} onClick={restart}><Trans>Back</Trans></Button>
            )}
            <Typography variant="h4" component="h1">{titles[step]}</Typography>
          </Stack>
          {step === 'choice' && (
            <Stack spacing={4}>
              <ActionChoice onChoose={start} onEmergency={handleEmergency} isCreatingEmergency={isCreatingEmergency} />
              <WaitingPatientsList />
            </Stack>
          )}
          {step === 'new-patient' && <NewPatientStep onCreated={choosePatient} onCancel={restart} />}
          {step === 'existing-patient' && <ExistingPatientStep onSelect={choosePatient} />}
          {step === 'waiting-room' && patient && <WaitingRoomStep patient={patient} onPaid={finish} />}
          {step === 'done' && <DoneStep patientLabel={patient?.label} waitingRoomName={waitingRoomName} consultationId={consultationId} onRestart={restart} />}
        </Paper>
      </Container>
    </Box>
  )
}
